/**
 * Flags an operator CAN reasonably change, but most never need to — page
 * deletion, the overlay and watermark tools, the transcription engine map.
 *
 * EXPECTED IN EVERY ENVIRONMENT, exactly like `standard.ts` — an absent one is
 * reported Missing and offered an Add. That is the whole line between this file
 * and `developer.ts`: a developer key is one we do NOT need registered
 * everywhere. These we do; they simply are not what anyone opens the page for,
 * so they get their own tab and stay out of All.
 */

import { ManifestDefinition } from '@redact-modules/admin/models';
export const ADVANCED_DEFINITIONS: readonly ManifestDefinition[] = [
  {
    /**
     * Put an organization back on Roboto, the app's font before VE-32221.
     *
     * A kill-switch, not a font picker. The app's font is Nunito Sans at 95%
     * width — the face the designs are drawn in, condensed on its width axis
     * because this app's screens are dense and that buys back what the 13px
     * row size costs. This flag only says "no thanks".
     *
     * Boolean rather than a font name on purpose. A font-name config needs
     * two defaults that do not agree — one for orgs with no override, one for
     * environments where the definition is not registered at all — and the
     * two are easy to confuse and hard to reason about together. A flag
     * defaulting to false has one answer: absent, `false`, and unregistered
     * all mean the same thing, so the new look is what every environment gets
     * until somebody deliberately opts out. That also makes registering this
     * definition optional rather than a prerequisite.
     *
     * The falsy-dropping in `selectApplicationConfigs` is harmless here for
     * the same reason. It cannot tell `false` from absent — and it does not
     * need to, because they are the same outcome. Contrast the kill-switches
     * in `user-permissions/selectors.ts`, which read the RAW records through
     * `booleanAppConfig` precisely because for them the two differ.
     */
    configKey: 'useRobotoFont',
    configType: 'Boolean',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description: 'Redact: use the legacy Roboto font instead of Nunito Sans',
    defaultValue: 'false',
  },
  {
    configKey: 'enableTextOverlay',
    configType: 'Boolean',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description: 'Feature Flag: On-screen text overlay tool (default on)',
    defaultValue: 'true',
  },
  {
    configKey: 'enableDeleteNonEmptyFolder',
    configType: 'Boolean',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description:
      'Feature Flag: Allow deleting folders/requests that still contain files',
    defaultValue: 'false',
  },
  {
    /**
     * A map of language code to engine id, spread OVER the app's own table of
     * Speechmatics engines (`utils/speechmatics/engines.ts`). An entry here
     * replaces the built-in engine for that language; a language the table
     * does not cover is added by naming it.
     *
     * `"{}"` is a SAFE default and means "change nothing": the app ships
     * engines for its own languages, so an empty map is the normal state, not
     * a broken one. That was not true before — the dropdown's options used to
     * BE this map's keys, so an org without one had no languages at all.
     *
     * Keys are language codes as `utils/speechmatics/index.ts` lists them
     * (`en`, `es`, `fr`, `fa`, …), NOT country codes. A map keyed `us` silently
     * matches nothing and leaves every language on the built-in engine.
     */
    configKey: 'transcriptionEngines',
    configType: 'JSON',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description: 'Map of language to transcription engine id.',
    defaultValue: '{}',
  },
  {
    /**
     * The same thing for speaker separation, which Speechmatics also ships one
     * engine per language of.
     *
     * Added at the same time as the built-in table it overrides, deliberately:
     * `speakerSeparationEngineId` was a scalar, and the scalar is what made
     * separation English-only — a French transcript sent to the English
     * separation model comes back with boundaries drawn from the wrong
     * acoustics, so the capability was withheld rather than shipped wrong.
     * A map is what lets it be offered per language, and getting the shape
     * right before orgs configure the scalar is what keeps it from becoming a
     * migration.
     */
    configKey: 'speakerSeparationEngines',
    configType: 'JSON',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description: 'Map of language to speaker separation engine id.',
    defaultValue: '{}',
  },
  {
    /**
     * JSON-typed and declares no default; the app supplies its own options.
     */
    configKey: 'transcriptionEngineOptions',
    configType: 'JSON',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description: 'Redact: Transcription Engine Options',
  },
  {
    configKey: 'rootFolderSmartSelection',
    configType: 'Boolean',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description:
      'Feature Flag: when several owner-less root folders are returned, resolve the org root by child-folder count instead of taking the first',
    defaultValue: 'false',
  },
  {
    /**
     * NO DEFAULT, deliberately — and that is what keeps the app's own engine
     * id in charge.
     *
     * `engines/selectors.ts` hard-codes the transcription engine and merges
     * any application-config value OVER it. A `defaultValue` here would
     * therefore resolve for every organization that has not set its own
     * (`applicationConfig` returns a record per org with `includeDefaults`),
     * silently repointing transcription everywhere this definition is
     * registered. It carried `01e54769-c448-4ece-a500-4fab5b49b722` for
     * exactly that reason once, back when the id came from each environment's
     * `config_*.js`; now that the app ships the id itself, a default here
     * could only fight it.
     *
     * Same shape as `voiceMaskEngineId` and `speakerSeparationEngineId`:
     * registered so an org HAS the knob, empty so the app decides until
     * someone turns it.
     *
     * Those two sit in `developer.ts`; this one stays here, where absence is
     * reported Missing, because customers DO turn it — some run a
     * transcription engine that is not Speechmatics at all. A knob with live
     * users is one every environment should have registered, whatever its
     * default says.
     */
    configKey: 'transcriptionEngineId',
    configType: 'String',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description:
      'Redact: Engine Id to use for Transcription. Overrides the engine id built into the app; leave unset to use that.',
  },
  {
    configKey: 'useGPU',
    configType: 'Boolean',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description: 'Feature Flag: Use GPU detection engines',
    defaultValue: 'false',
  },
];
